// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { checkAuthentication, getLastAdvisorStatus } from '../utils/auth';
import { useTokenRefresh } from '../hooks/useTokenRefresh';
import { AnalyticsIdentity } from '@loa/analytics';
import { AdvisorProvider, useAdvisor } from '../contexts/AdvisorContext';
import { NotificationProvider } from '../contexts/NotificationContext';
import { ToastProvider } from '@loa/ui-components';
import { Layout } from './Layout';
import { SplashScreen } from './SplashScreen';
import { PageHeaderProvider } from '../contexts/PageHeaderContext';
import { TagsProvider } from '../contexts/TagsContext';
import { CompleteProfileModal } from './CompleteProfileModal';

/**
 * AuthenticatedRouteLayout - Single wrapper for all protected routes
 *
 * This component:
 * 1. Checks authentication once (not on every navigation)
 * 2. If not authenticated, redirects to login
 * 3. Provides AdvisorProvider context (loads advisor profile once)
 * 4. Provides NotificationProvider context (maintains SSE connection)
 * 5. Renders Layout with sidebar and header (persists across navigation)
 * 6. Uses <Outlet /> to render child routes
 *
 * Benefits:
 * - No loading flash between pages
 * - SSE connection stays open during navigation
 * - Advisor profile fetched once, shared via context
 * - Layout doesn't remount on navigation
 * - Proactive token refresh before expiry
 */
function AdvisorAnalyticsIdentity() {
  const { advisor } = useAdvisor();
  return <AnalyticsIdentity userId={advisor?.id?.toString()} role={advisor?.role} />;
}

/**
 * Renders the blocking CompleteProfileModal when an active advisor lands
 * with a missing phone number. SSO-provisioned advisors often arrive
 * without a phone_number JWT claim (Microsoft rarely sends one; Google
 * only sends it when the brokerage's Workspace admin has surfaced the
 * scope). Mounts inside AdvisorProvider so useAdvisor + refetch work.
 */
function ProfileCompletionGuard() {
  const { advisor, refetch } = useAdvisor();
  if (!advisor) return null;
  const phone = advisor.phoneNumber?.trim();
  if (phone && phone.length > 0) return null;
  return (
    <CompleteProfileModal
      advisor={advisor}
      onSaved={() => { void refetch(); }}
    />
  );
}

export function AuthenticatedRouteLayout() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isChecking, setIsChecking] = useState(true);
  const [showSplash, setShowSplash] = useState(true);
  const statusMessage = 'Verifying authentication...';
  const location = useLocation();

  // Enable proactive token refresh once authenticated
  // This runs in the background to prevent session expiry during active use
  // Proactive token refresh - uses default 45s interval to keep BFF session warm
  // and trigger server-side access token refresh before it expires
  useTokenRefresh({
    enabled: isAuthenticated === true,
    onRefreshSuccess: () => {
      console.log('Proactive token refresh succeeded');
    },
    onRefreshFail: (error) => {
      console.log('Proactive token refresh failed:', error);
      // Don't redirect here - let the Axios interceptor handle it on next API call
    },
  });

  useEffect(() => {
    const verifyAuth = async () => {
      try {
        const authenticated = await checkAuthentication();
        setIsAuthenticated(authenticated);
      } catch (error) {
        console.error('Error verifying authentication:', error);
        setIsAuthenticated(false);
      } finally {
        setIsChecking(false);
      }
    };

    verifyAuth();
  }, []);

  useEffect(() => {
    if (!isChecking) {
      const timer = setTimeout(() => setShowSplash(false), 1500);
      return () => clearTimeout(timer);
    }
  }, [isChecking]);

  // Show splash screen while checking authentication + 1.5s after
  if (isChecking || showSplash) {
    return <SplashScreen message={statusMessage} />;
  }

  // If not authenticated, redirect to login with return URL
  if (!isAuthenticated) {
    const returnUrl = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?returnUrl=${returnUrl}`} replace />;
  }

  // SSO-provisioned advisors at brokerages with REQUIRE_ADMIN_APPROVAL
  // (or any advisor whose Keycloak realm role doesn't include the
  // ROLE_USER mapping the dashboard endpoints need) land here on first
  // sign-in. Redirect BEFORE AdvisorProvider mounts — that provider
  // fetches /api/advisors/me which 403s for pending advisors and
  // cascades errors through the dashboard. AuthenticatedRoute does the
  // same check for the standalone /register + /awaiting-approval routes
  // outside the layout; this is the matching guard for the layout.
  if (getLastAdvisorStatus() === 'pending_activation'
      && location.pathname !== '/awaiting-approval') {
    return <Navigate to="/awaiting-approval" replace />;
  }

  // Authenticated - render the layout with providers
  return (
    <AdvisorProvider>
      <AdvisorAnalyticsIdentity />
      <NotificationProvider>
        <ToastProvider>
          <PageHeaderProvider>
            <TagsProvider>
              <Layout>
                <Outlet />
              </Layout>
              <ProfileCompletionGuard />
            </TagsProvider>
          </PageHeaderProvider>
        </ToastProvider>
      </NotificationProvider>
    </AdvisorProvider>
  );
}
